import {
  Card,
  CardType,
  SubbookId,
  createCard,
  CARD_DEFINITIONS,
  getCardCost,
} from "@/program/Card";
import type { OrbColor } from "@/program/VariableState";
import { CardListEngine, PALETTE_MIME_TYPE } from "./CardListEngine";

/** モーダルオーバーレイのセレクタ（ESC競合回避用） */
const MODAL_OVERLAY_SELECTOR = [
  ".condition-modal-overlay",
  ".step-modal-overlay",
  ".orb-modal-overlay",
  ".turn-modal-overlay",
  ".subbook-selector-modal-overlay",
].join(", ");

/**
 * プログラムエリア - カードを並べてプログラムを構成
 *
 * if カードのネスト構造に対応。
 * ターゲットモードで「アクティブなブランチ」にカードを追加する。
 *
 * 共通のカード操作ロジックは CardListEngine に委譲し、
 * このクラスは MP 管理・コールバック通知などの固有ロジックのみ担う。
 */
export class ProgramArea {
  private container: HTMLElement;
  private engine: CardListEngine;
  private maxMP: number = Infinity;
  private onChangeCallback?: (cards: Card[]) => void;
  private onMPChangeCallback?: (currentCost: number, maxMP: number) => void;
  private onTargetChangeCallback?: () => void;
  private onPaletteBranchDropCallback?: (
    branchId: string,
    cardType: string,
    insertionIndex?: number
  ) => void;

  constructor(parentElement: HTMLElement) {
    this.container = document.createElement("div");
    this.container.className = "program-area";
    this.container.setAttribute("aria-label", "プログラムエリア");
    parentElement.appendChild(this.container);

    this.engine = new CardListEngine({
      mimeType: "application/x-questile-program",
      rootDropContainer: this.container,
      useIconSVG: true,
      onCardsChanged: () => {
        this.notifyChange();
        this.notifyMPChange();
      },
      onDropComplete: (cardId) => {
        this.flashDroppedCard(cardId);
      },
      getAvailableMP: (card) => {
        return this.maxMP - (this.getTotalCost() - getCardCost(card));
      },
      getMaxMP: () => this.maxMP,
      renderSuffix: () => {
        const dropTail = document.createElement("div");
        dropTail.className = "program-area__drop-tail";
        return [dropTail];
      },
      placeholderText: "呪文をクリックして追加",
      onPaletteDropToBranch: (
        branchId: string,
        cardType: string,
        insertionIndex?: number
      ) => {
        this.onPaletteBranchDropCallback?.(branchId, cardType, insertionIndex);
      },
    });

    // コンテナまたはプレースホルダーのクリックでターゲットモードをリセット
    this.container.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest(".card-d")) return;
      if (this.engine.getActiveContainerId() !== null) {
        this.setActiveContainer(null);
      } else {
        this.onTargetChangeCallback?.();
      }
    });

    // ESCキーでターゲットモードをリセット（モーダルが開いていない場合のみ）
    document.addEventListener("keydown", this.handleEscKey);

    this.engine.render();
  }

  private handleEscKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.engine.getActiveContainerId() !== null) {
      const hasOpenModal = document.querySelector(MODAL_OVERLAY_SELECTOR);
      if (!hasOpenModal) {
        this.setActiveContainer(null);
      }
    }
  };

  /**
   * カードを追加（アクティブなブランチに追加される）
   * @returns 追加できたかどうか（MP超過の場合はfalse）
   */
  addCard(type: CardType, subbookId?: SubbookId): boolean {
    return this.addCardInstance(
      createCard(type, undefined, undefined, subbookId)
    );
  }

  /**
   * オーブカードを値・色指定で追加
   * @returns 追加できたかどうか（MP超過の場合はfalse）
   */
  addCardWithProps(
    type: "addToOrb" | "multiplyOrb",
    value: number,
    orbColor: OrbColor
  ): boolean {
    return this.addCardInstance(createCard(type, value, orbColor));
  }

  /**
   * パレットからのD&Dドロップを受け付ける
   * コールバックには挿入インデックスが渡される（黄色い線の位置に対応）
   */
  onPaletteDrop(
    callback: (type: CardType, insertionIndex?: number) => void
  ): void {
    this.container.addEventListener("dragover", (e) => {
      if (e.dataTransfer?.types.includes(PALETTE_MIME_TYPE)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }
    });
    this.container.addEventListener("drop", (e) => {
      const type = e.dataTransfer?.getData(PALETTE_MIME_TYPE);
      if (type && type in CARD_DEFINITIONS) {
        e.stopPropagation();
        // ルートコンテナへのドロップはブランチへの追加ではないため
        // activeContainerId をリセットしてルートに追加させる
        this.setActiveContainer(null, false);
        // ドロップ位置（黄色い線の位置）から挿入インデックスを計算して渡す
        const insertionIndex = this.engine.findInsertionIndex(
          this.container,
          e.clientY
        );
        callback(type as CardType, insertionIndex);
      }
    });
  }

  /**
   * 構築済みCardオブジェクトを追加（モーダルで設定済みのカード用）
   * @param insertionIndex D&Dで指定された挿入位置（省略時は末尾）
   * @returns 追加できたかどうか（MP超過の場合はfalse）
   */
  addCardDirect(card: Card, insertionIndex?: number): boolean {
    return this.addCardInstance(card, insertionIndex);
  }

  private addCardInstance(card: Card, insertionIndex?: number): boolean {
    const newTotalCost = this.getTotalCost() + getCardCost(card);

    if (newTotalCost > this.maxMP) {
      return false;
    }

    const activeId = this.engine.getActiveContainerId();
    if (activeId) {
      const branch = this.engine.findBranch(activeId);
      if (branch) {
        this.engine.addCardToBranch(activeId, card, insertionIndex);
      } else {
        // 無効なコンテナ → ルートにフォールバック
        this.engine.setActiveContainerId(null);
        this.engine.addCardToRoot(card, insertionIndex);
      }
    } else {
      this.engine.addCardToRoot(card, insertionIndex);
    }

    return true;
  }

  /**
   * パネルを表示する
   */
  show(): void {
    this.container.style.display = "";
  }

  /**
   * パネルを非表示にする
   */
  hide(): void {
    this.container.style.display = "none";
  }

  /**
   * WAI-ARIA タブパネルとして設定する
   */
  configureTabPanel(id: string, labelledBy: string): void {
    this.container.id = id;
    this.container.setAttribute("role", "tabpanel");
    this.container.setAttribute("aria-labelledby", labelledBy);
    this.container.removeAttribute("aria-label");
  }

  /**
   * カードを削除（ネスト構造を再帰的に探索）
   */
  removeCard(cardId: string): void {
    this.engine.removeCard(cardId);
  }

  /**
   * 全カードをクリア
   */
  clear(): void {
    this.engine.clear();
  }

  /**
   * カード配列を取得
   */
  getCards(): Card[] {
    return this.engine.getCards();
  }

  /**
   * 変更時コールバックを設定
   */
  onChange(callback: (cards: Card[]) => void): void {
    this.onChangeCallback = callback;
  }

  /**
   * MP変更時コールバックを設定
   */
  onMPChange(callback: (currentCost: number, maxMP: number) => void): void {
    this.onMPChangeCallback = callback;
  }

  /**
   * callカードの編集モーダルで表示するサブブックスロット数を設定
   */
  setSubbookSlots(slots: number): void {
    this.engine.setSubbookSlots(slots);
  }

  /**
   * 最大MPを設定
   *
   * 注意: 既存カードのコストが新しいMP上限を超えていてもチェック・調整しない。
   * ステージ切り替え時など既存カードがある状態でMPを変更する場合は、
   * 先に clear() を呼んでカードをクリアすることを推奨。
   */
  setMaxMP(mp: number): void {
    this.maxMP = mp;
    this.notifyMPChange();
  }

  /**
   * 現在の最大MPを取得
   */
  getMaxMP(): number {
    return this.maxMP;
  }

  /**
   * 現在の総コストを取得
   */
  getTotalCost(): number {
    return this.engine.getTotalCost();
  }

  /**
   * 指定タイプのカードを追加できるか判定
   */
  canAddCard(type: CardType): boolean {
    const cost = CARD_DEFINITIONS[type].cost;
    return this.getTotalCost() + cost <= this.maxMP;
  }

  /**
   * ターゲットモード: アクティブコンテナを設定
   */
  setActiveContainer(branchId: string | null, notify = true): void {
    this.engine.setActiveContainerId(branchId);
    this.engine.render();
    if (notify) {
      this.onTargetChangeCallback?.();
    }
  }

  /**
   * ターゲットモード変更時のコールバックを設定
   */
  onTargetChange(callback: () => void): void {
    this.onTargetChangeCallback = callback;
  }

  /**
   * パレットからブランチへD&Dされたときのコールバックを設定
   * （CardListEngineがactiveContainerIdをbranchIdにセット後に呼ばれる）
   * insertionIndex はドロップ位置に基づく挿入インデックス（省略時は末尾）
   */
  onPaletteBranchDrop(
    callback: (
      branchId: string,
      cardType: string,
      insertionIndex?: number
    ) => void
  ): void {
    this.onPaletteBranchDropCallback = callback;
  }

  /**
   * ターゲットモード: アクティブコンテナIDを取得
   */
  getActiveContainerId(): string | null {
    return this.engine.getActiveContainerId();
  }

  /**
   * 指定カードIDをハイライト（実行中表示）
   */
  highlightCard(cardId: string): void {
    this.engine.highlightCard(cardId);
  }

  /**
   * 全カードのハイライトを解除
   */
  clearHighlight(): void {
    this.engine.clearHighlight();
  }

  /**
   * カード移動（テスト用publicインターフェース）
   */
  moveCardById(
    draggedId: string,
    targetId: string,
    position: "before" | "after"
  ): void {
    this.engine.moveCard(draggedId, targetId, position);
  }

  /**
   * カードをブランチに移動（テスト用publicインターフェース）
   */
  moveCardToBranchById(
    draggedId: string,
    branchId: string,
    index?: number
  ): void {
    this.engine.moveCardToBranch(draggedId, branchId, index);
  }

  /**
   * カードをルートに移動（テスト用publicインターフェース）
   */
  moveCardToRootById(draggedId: string, index?: number): void {
    this.engine.moveCardToRoot(draggedId, index);
  }

  // ─── 内部メソッド ───

  /**
   * ドロップ完了時のフラッシュアニメーション
   */
  private flashDroppedCard(cardId: string): void {
    const el = this.container.querySelector(
      `[data-card-id="${CSS.escape(cardId)}"]`
    ) as HTMLElement | null;
    if (!el) return;

    el.classList.remove("card-d--just-dropped");
    void el.offsetWidth; // force reflow

    el.classList.add("card-d--just-dropped");
    el.addEventListener(
      "animationend",
      () => el.classList.remove("card-d--just-dropped"),
      { once: true }
    );

    // prefers-reduced-motion 時にanimationendが発火しない場合のフォールバック
    setTimeout(() => {
      el.classList.remove("card-d--just-dropped");
    }, 600);
  }

  private notifyChange(): void {
    if (this.onChangeCallback) {
      this.onChangeCallback(this.engine.getCards());
    }
  }

  private notifyMPChange(): void {
    if (this.onMPChangeCallback) {
      this.onMPChangeCallback(this.getTotalCost(), this.maxMP);
    }
  }
}
